/**
 * Einstellungen je Apartment (Board „Preisautomatik · Einstellungen“): Abschnitte, Freigabeweg und
 * Aktionsleiste. Erst prüft das Formular, dann der Server; die Nutzlast entsteht in `toApiInput`.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { ConfigurationActionBar } from './ConfigurationActionBar';
import { ConfigurationDiscardDialog } from './ConfigurationDiscardDialog';
import {
  ConfigurationIssueSummary,
  ConfigurationReplacedNotice,
} from './ConfigurationIssueSummary';
import { ConfigurationReleaseRail } from './ConfigurationReleaseRail';
import { ConfigurationSectionNavigation } from './ConfigurationSectionNavigation';
import { PricingConfigurationFields } from './PricingConfigurationFields';
import { ConfigurationPreview } from './ConfigurationPreview';
import {
  pricingConfigurationFormSchema,
  toFormValues,
  type PricingConfigurationFormValues,
} from './pricingConfigurationSchema';
import type { PricingConfigurationFormProps } from './pricingConfigurationFormProps';
import { releaseInput } from './releaseInput';
import { barText, releaseSteps } from './releaseRailModel';
import { useConfigurationIssues } from './useConfigurationIssues';
import { useConfigurationReset } from './useConfigurationReset';
import { usePricingPreviewFlow } from './usePricingPreviewFlow';

export function PricingConfigurationForm(props: PricingConfigurationFormProps) {
  const form = useForm<PricingConfigurationFormValues>({
    resolver: zodResolver(pricingConfigurationFormSchema),
    defaultValues: toFormValues(props.configuration),
  });
  const windows = useFieldArray({ control: form.control, name: 'publicationWindows' });
  const tiers = useFieldArray({ control: form.control, name: 'minimumStayLeadTimeTiers' });
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const mode = form.watch('mode');
  const dirty = form.formState.isDirty;
  const draftKey = JSON.stringify(form.watch());
  const issues = useConfigurationIssues(form, draftKey, mode, props.validation);
  const { onValidate, onSave } = props;
  const flow = usePricingPreviewFlow({
    form,
    configuration: props.configuration,
    saveErrorCode: props.saveErrorCode,
    // Ein Befund gilt dem Entwurf beim Klick, und erst wenn die Antwort da ist.
    onValidate:
      onValidate &&
      (async (input) => {
        const key = draftKey;
        const result = await onValidate(input);
        if (result) issues.markValidated(key);
        return result;
      }),
    onPreview: props.onPreview,
    onSave: async (...args) => {
      const key = draftKey;
      const result = await onSave(...args);
      if (result === 'rejected') issues.markValidated(key);
      return result;
    },
    onRejected: (source) => issues.rejected(source),
  });
  const replacedBy = useConfigurationReset(
    form,
    props.configuration,
    props.versionNumber,
    props.saved?.versionNumber,
    props.onDirtyChange,
  );
  const release = releaseInput(props, {
    mode,
    dirty,
    changed: issues.changed,
    previewAt: flow.freshPreview?.calculatedAtUtc,
  });
  const bar = barText(release);
  const off = mode === 'Off';

  return (
    <form onSubmit={(event) => event.preventDefault()} className="flex min-w-0 flex-col gap-4">
      <div className="@container">
        <div className="@5xl:grid-cols-[200px_minmax(0,1fr)_260px] grid items-start gap-4">
          <ConfigurationSectionNavigation markers={issues.markers} />
          <div className="flex min-w-0 flex-col gap-4">
            <ConfigurationReplacedNotice version={replacedBy} />
            <ConfigurationIssueSummary issues={issues.summary} server={issues.summaryFromServer} />
            <PricingConfigurationFields
              {...props}
              form={form}
              windows={windows}
              tiers={tiers}
              markers={issues.markers}
              serverByField={issues.serverByField}
              serverErrors={issues.serverErrors}
              serverChecked={issues.serverChecked}
              publicationControlsAvailable={props.publicationControlsAvailable ?? false}
            />
          </div>
          <ConfigurationReleaseRail
            steps={releaseSteps(release)}
            publicationControlsAvailable={props.publicationControlsAvailable ?? false}
          />
        </div>
      </div>
      <ConfigurationActionBar
        title={bar.title}
        subline={bar.subline}
        off={off}
        changed={dirty}
        busy={Boolean(props.busy)}
        problem={props.problem}
        onReload={props.onReload}
        onDiscard={() => setConfirmDiscard(true)}
        onPrimary={off ? () => void flow.savePaused() : flow.openPreview}
      />
      <ConfigurationDiscardDialog
        open={confirmDiscard}
        switching={false}
        onKeep={() => setConfirmDiscard(false)}
        onDiscard={() => {
          setConfirmDiscard(false);
          form.reset(toFormValues(props.configuration));
          flow.setDialogOpen(false);
          issues.reset();
        }}
      />
      <ConfigurationPreview flow={flow} props={props} />
    </form>
  );
}
